import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import api from '../services/api';
import LarForm from '../components/Lar/LarForm';
import Pagination from '../components/Pagination';

// Cores para o status do lar (baseado no campo active)
const STATUS_COLORS = {
  true: 'success',
  false: 'error'
};

// Labels para o status do lar (baseado no campo active)
const STATUS_LABELS = {
  true: 'Ativo',
  false: 'Inativo'
};

const Lares = () => {
  const [lares, setLares] = useState([]);
  const [selectedLar, setSelectedLar] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const user = JSON.parse(localStorage.getItem('user'));
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    carregarLares();
  }, [page, searchTerm]);

  const carregarLares = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/lares', {
        params: {
          page,
          search: searchTerm,
          limit: 10
        }
      });
      setLares(response.data.lares);
      setTotalPages(response.data.pages);
      setError(null);
    } catch (error) {
      console.error('Erro ao carregar lares:', error);
      if (error.response?.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
      } else {
        setError('Erro ao carregar lares. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (lar = null, view = false) => {
    setSelectedLar(lar);
    setViewMode(view);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedLar(null);
    setViewMode(false);
    setOpenDialog(false);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedLar) {
        await api.put(`/api/lares/${selectedLar.id}`, formData);
        enqueueSnackbar('Lar atualizado com sucesso!', { variant: 'success' });
      } else {
        await api.post('/api/lares', formData);
        enqueueSnackbar('Novo lar criado com sucesso!', { variant: 'success' });
      }
      handleCloseDialog();
      carregarLares();
      setError(null);
    } catch (error) {
      console.error('Erro ao salvar lar:', error);
      if (error.response?.status === 403) {
        enqueueSnackbar('Você não tem permissão para realizar esta ação.', { variant: 'error' });
        throw new Error('Você não tem permissão para realizar esta ação.');
      }
      enqueueSnackbar(error.response?.data?.message || 'Erro ao salvar lar. Por favor, tente novamente.', { variant: 'error' });
      throw error;
    }
  };

  const canCreate = user?.profile === 'Administrador';
  const canEdit = (lar) => {
    if (user?.profile === 'Administrador') return true;
    if (user?.profile === 'Gestor' && user?.larId === lar.id) return true;
    return false;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Gerenciamento de Lares
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Lar
          </Button>
        )}
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Buscar"
            variant="outlined"
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Buscar por nome, cidade ou observações"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ height: '56px', minWidth: '120px' }}
          >
            Buscar
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : lares.length === 0 ? (
        <Alert severity="info">
          Nenhum lar encontrado.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Cidade/UF</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lares.map((lar) => (
                <TableRow key={lar.id}>
                  <TableCell>{lar.nome}</TableCell>
                  <TableCell>{lar.email}</TableCell>
                  <TableCell>{`${lar.cidade}/${lar.estado}`}</TableCell>
                  <TableCell>
                    <Tooltip title={lar.active ? 'Lar está ativo no sistema' : 'Lar está inativo no sistema'}>
                      <Chip
                        label={STATUS_LABELS[lar.active]}
                        color={STATUS_COLORS[lar.active]}
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(lar, true)}
                      title="Visualizar"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {canEdit(lar) && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(lar)}
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={2}>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {viewMode
            ? 'Visualizar Lar'
            : selectedLar
            ? 'Editar Lar'
            : 'Novo Lar'}
        </DialogTitle>
        <DialogContent>
          <LarForm
            lar={selectedLar}
            onSubmit={handleSubmit}
            readOnly={viewMode}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Lares;
